import {Box, Button, DialogActions, Grid, IconButton, Snackbar, styled, TextField, Typography} from '@mui/material';
import {Fragment, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {UpiIdSeemsInvalidError} from '../../api/error';
import CloseIcon from '@mui/icons-material/Close';
import {updatePaymentDetails} from '../../api/payments';
import {APIResponse, HttpResponse, MetaDataKeys, MetaInfoType} from '../../api/models';
import {useEventBatching} from '../../api/events';
import {AxiosError} from 'axios';

const BackgroundBoxWrapper = styled(Box)<{backgroundColor: string}>(({backgroundColor}) => ({
    paddingTop: 32,
    paddingLeft: 54,
    paddingRight: 54,
    paddingBottom: 32,
    margin: 0,
    backgroundColor: backgroundColor,
}));

export const PaymentDashboardScreen = () => {
    const [upiId, setUpiId] = useState<string>('');
    const [open, setOpen] = useState(false);
    const [snackBarMessage, updateSnackbarMessage] = useState<string>('');
    const {trackEvent} = useEventBatching();

    const navigateTo = useNavigate();

    const throwSnackbarMessage = (message: string) => {
        updateSnackbarMessage(message);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleDismissFn = () => {
        navigateTo('/dashboard-screen');
    };

    const validateUpiId = (upiId: string | undefined, typeName: string) => {
        if (upiId) {
            const regexp = /^[a-zA-Z0-9_.]+@[a-zA-Z0-9]+$/;
            if (!regexp.test(upiId)) {
                throw new UpiIdSeemsInvalidError(
                    `The ${typeName} must be in the format username@domain (e.g., example@domain)`,
                );
            }
        } else {
            throw new UpiIdSeemsInvalidError(`${typeName} cannot be empty`);
        }
    };

    const validateInfo = (): boolean => {
        try {
            validateUpiId(upiId, 'Upi id');
            return true;
        } catch (error) {
            if (error instanceof UpiIdSeemsInvalidError) {
                throwSnackbarMessage(error.message);
            }
            return false;
        }
    };

    const handleSubmit = async () => {
        const isValid = validateInfo();
        if (isValid) {
            const upiDetailsUpdateRequestDTO: MetaInfoType = {
                type: MetaDataKeys.paymentVpa,
                upiId: upiId,
            };
            try {
                await updatePaymentDetails(upiDetailsUpdateRequestDTO);
                navigateTo('/dashboard-screen');
            } catch (error) {
                if (error instanceof AxiosError) {
                    const message = error.response?.data as APIResponse;
                    throwSnackbarMessage((message.data as HttpResponse).message);
                    trackEvent({
                        eventName: 'Payment Details VPA/GST Update Failed',
                        props: {
                            error: error.toString(),
                            errorMessage: (message.data as HttpResponse).message,
                            type: upiDetailsUpdateRequestDTO.type,
                            upiId: upiDetailsUpdateRequestDTO.upiId,
                        },
                    });
                }
            }
        }
    };

    const snackBarMessageHandler = (
        <Fragment>
            <Button size='small' onClick={handleClose}>
                Dismiss
            </Button>
            <IconButton size='small' aria-label='close' color='inherit' onClick={handleClose}>
                <CloseIcon fontSize='small' />
            </IconButton>
        </Fragment>
    );

    return (
        <div>
            <BackgroundBoxWrapper backgroundColor={'white'}>
                <Grid style={{marginTop: 42}} container>
                    <Grid item md={12} xs={12} sm={12}>
                        <Typography
                            sx={{
                                marginTop: 1,
                                fontWeight: 700,
                                color: 'black',
                                fontFamily: 'Poppins',
                            }}
                            variant='body1'>
                            Update VPA/UPI Information
                        </Typography>
                    </Grid>
                </Grid>
                <Grid style={{marginTop: 12}} container>
                    <Grid item md={12} xs={12} sm={12}>
                        <TextField
                            style={{
                                color: 'black',
                            }}
                            id={''}
                            label='Upi Id'
                            variant='outlined'
                            fullWidth
                            value={upiId}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                setUpiId(e.target.value);
                            }}
                        />
                    </Grid>
                </Grid>
                <Grid style={{marginTop: 12}} container>
                    <Grid item md={4} xs={4} sm={4}>
                        <DialogActions>
                            <Button onClick={handleSubmit}>Update changes</Button>
                            <Button onClick={handleDismissFn}>Dismiss</Button>
                        </DialogActions>
                    </Grid>
                </Grid>
            </BackgroundBoxWrapper>
            <Snackbar
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                open={open}
                action={snackBarMessageHandler}
                autoHideDuration={2500}
                message={snackBarMessage}
            />
        </div>
    );
};
